import { Badge, Banner } from "@design";
import { useQuery } from "@tanstack/react-query";
import { Plug } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import type { ConnectorSummary } from "@/api";
import { getConnectorPolicyOptions, listConnectorsOptions } from "@/api";
import { EmptyState } from "@/components/build/EmptyState";
import { EntityListPage } from "@/components/build/EntityListPage";
import { EntityRow } from "@/components/build/EntityRow";
import { byName, byNewest, useListControls } from "@/components/build/useListControls";
import { ConnectorIcon } from "./ConnectorIcon";
import { useDeleteConnector } from "./useDeleteConnector";

const isMine = (c: ConnectorSummary) => !c.isShared;

function RowBadges({ connector }: Readonly<{ connector: ConnectorSummary }>) {
  const { t } = useTranslation("connectors");
  return (
    <span className="flex flex-wrap items-center justify-end gap-1">
      <Badge size="compact">{t(`row.${connector.transport === "http" ? "http" : "mcp"}`)}</Badge>
      {connector.disabledByAdmin && (
        <Badge size="compact" variant="danger">
          {t("row.disabledByAdmin")}
        </Badge>
      )}
      {!connector.disabledByAdmin && !connector.isEnabled && <Badge size="compact">{t("row.off")}</Badge>}
      {connector.lastError && (
        <Badge size="compact" variant="warning">
          {t("row.problem")}
        </Badge>
      )}
    </span>
  );
}

/** Build > Connectors: MCP servers and API endpoints I added or that were shared with me. */
export function ConnectorsListPage() {
  const { t } = useTranslation(["build", "connectors"]);
  const navigate = useNavigate();
  const { onDelete, dialog } = useDeleteConnector();
  const policy = useQuery({ ...getConnectorPolicyOptions(), select: (r) => r.data });
  const { search, apply, listProps } = useListControls<ConnectorSummary>({
    isMine,
    sorts: [
      { value: "newest", label: t("build:list.sort.newest"), compare: byNewest },
      { value: "name", label: t("build:list.sort.name"), compare: byName },
    ],
  });
  const query = useQuery(listConnectorsOptions({ query: { search: search || undefined, page_size: 100 } }));
  const connectors = apply(query.data?.data, { searched: true });
  const canAdd = policy.data?.canAdd ?? true;

  return (
    <EntityListPage
      title={t("connectors:title")}
      subtitle={t("connectors:subtitle")}
      newLabel={t("connectors:new")}
      onNew={() => navigate("/build/connectors/new")}
      canCreate={canAdd}
      {...listProps}
    >
      {!canAdd && <Banner variant="info" message={t("connectors:addingOff")} />}
      {connectors.map((connector) => (
        <EntityRow
          key={connector.uuid}
          icon={<ConnectorIcon transport={connector.transport} />}
          name={connector.name}
          description={
            connector.isShared && connector.createdByName
              ? `${connector.url} · ${t("connectors:row.sharedBy", { name: connector.createdByName })}`
              : connector.url
          }
          meta={<RowBadges connector={connector} />}
          onOpen={() => navigate(`/build/connectors/${connector.uuid}`)}
          menuItems={[
            { label: t("build:menu.edit"), onSelect: () => navigate(`/build/connectors/${connector.uuid}`) },
            ...(connector.currentUserRole === "admin"
              ? [{ label: t("build:menu.delete"), danger: true, onSelect: () => onDelete(connector) }]
              : []),
          ]}
        />
      ))}
      {query.isSuccess &&
        connectors.length === 0 &&
        (search ? (
          <EmptyState title={t("build:list.noResultsTitle")} body={t("build:list.noResultsBody", { query: search })} />
        ) : (
          <EmptyState icon={<Plug size={32} />} title={t("connectors:empty.title")} body={t("connectors:empty.body")} />
        ))}
      {dialog}
    </EntityListPage>
  );
}
